/**
 * Phase 6 cross-cutting PR 2b — `<EscalationSummaryModal>`.
 *
 * In-place modal that opens when an `ai_escalation` row is clicked
 * in the bell dropdown. Replaces PR 2's interim fallback (which
 * navigated to `/v2/chat`).
 *
 * Per `direction.md` §1 DEVIATION row + D4:
 *   - Vanilla `TYPE_CONFIG.ai_escalation.nav = 'roster'` (trainer
 *     surface, deferred to Phase 6e — no athlete-side React equivalent).
 *   - React opens an in-place modal that preserves the caller-page
 *     context (the user stays on whatever route they were on when
 *     they clicked the notification), surfaces the escalation
 *     summary, and offers a secondary "Talk to coach →" CTA that
 *     routes to `/v2/chat`.
 *
 * The escalation "summary" content is the notification row's own
 * `title` + `message` fields (per OVERRIDES.md O5 — no new endpoint
 * or schema migration needed; the notification row already carries
 * enough info).
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styles from './EscalationSummaryModal.module.css';

interface EscalationSummaryModalProps {
  /** Notification title (e.g., "⚠️ Coach review requested"). */
  title: string;
  /** Notification body — the escalation summary text. */
  message: string | null;
  /** Close the modal (also fires when "Got it" is clicked). */
  onClose: () => void;
}

export function EscalationSummaryModal({
  title,
  message,
  onClose,
}: EscalationSummaryModalProps): JSX.Element {
  const navigate = useNavigate();

  // Esc closes the modal.
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleTalkToCoach = (): void => {
    onClose();
    navigate('/chat');
  };

  // B-33 — position: fixed modal rendered by the bell trigger inside the
  // sticky TopBar. Portal to document.body so the bar's transform: translateZ(0)
  // compositor layer can't become its containing block (which would strand this
  // fixed backdrop inside the 57px bar). Geometry is viewport-fixed either way.
  return createPortal(
    <div className={styles.backdrop} onMouseDown={handleBackdropMouseDown} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="escalation-title"
      >
        <div className={styles.header}>
          <h2 id="escalation-title" className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {message ? (
            <p className={styles.message}>{message}</p>
          ) : (
            <p className={styles.placeholder}>
              The full escalation context is in your chat with your coach.
            </p>
          )}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.gotItButton} onClick={onClose}>
            Got it
          </button>
          <button type="button" className={styles.talkButton} onClick={handleTalkToCoach}>
            Talk to coach ›
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
